import Currency from "../amount/currency";
import { EMAIL_REGEX, COUNTRY_REGEX, CHECKSUM_REGEX, PUBKEY_REGEX } from "../utils/regex";
import { call, isString, matchedString, resolveBoolean, withProperties } from "../utils";
import { isEmptyObject } from "../fetcher/support/utils";

const oneYear = 31536000;

const REQUIRED_PARAM = 0x1;

const EMAIL_PARAM = 0x2;
const PHONE_PARAM = 0x4; // to-do
const URL_PARAM = 0x8;
const PUBKEY_PARAM = 0x10;
const CHECKSUM_PARAM = 0x20;
const COUNTRY_PARAM = 0x40;
const CURRENCY_PARAM = 0x80; // might require refactoring
const BOOLEAN_PARAM = 0x100;
const BOOLEAN_DEFAULT_TRUE_PARAM = 0x200;
const NUMERIC_PARAM = 0x400;
const DURATION_PARAM = 0x600
const NUMBER_PARAM = 0x800;
const LIST_PARAM = 0x1000;
const AMOUNT_PARAM = 0x2000; // => 10 0000 0000 0000

const PARAMS = {
  amount: REQUIRED_PARAM | NUMBER_PARAM | AMOUNT_PARAM,
  campaign_id: 0,
  country: COUNTRY_PARAM,
  currency: REQUIRED_PARAM | CURRENCY_PARAM,
  customer_email: REQUIRED_PARAM | EMAIL_PARAM,
  customer_firstname: 0,
  customer_fullname: 0,
  customer_lastname: 0,
  customer_phone: PHONE_PARAM,
  custom_description: 0,
  custom_logo: URL_PARAM,
  custom_narration: 0,
  custom_title: 0,
  default_account: 0,
  disable_pwb: BOOLEAN_PARAM, // | BOOLEAN_DEFAULT_TRUE_PARAM,
  exclude_banks: LIST_PARAM | NUMERIC_PARAM,
  custom_pwbt_expiry: DURATION_PARAM,
  include_discount_amount: BOOLEAN_PARAM | BOOLEAN_DEFAULT_TRUE_PARAM,
  init_url: URL_PARAM,
  integrity_hash: CHECKSUM_PARAM,
  checkout_hash: CHECKSUM_PARAM,
  payload_hash: CHECKSUM_PARAM,
  is_event_tx: NUMERIC_PARAM,
  mobilemoney_network: 0,
  payment_method: 0,
  payment_options: LIST_PARAM,
  payment_page: 0,
  payment_plan: NUMBER_PARAM,
  PBFPubKey: REQUIRED_PARAM | PUBKEY_PARAM,
  recurring_stop: 0,
  redirect_no_json: BOOLEAN_PARAM,
  redirect_post: BOOLEAN_PARAM,
  redirect_url: URL_PARAM,
  settlement_token: 0,
  txref: REQUIRED_PARAM,
  custom_session_duration : DURATION_PARAM,
  custom_retry: NUMBER_PARAM,
  merchant_vat_amount: NUMBER_PARAM | AMOUNT_PARAM,
}

const parser = (function __parser__(flag, value) {
  const $value = isString(value) && value.trim() || null;
  const __error__ = __parseError__.bind((this.value = $value || value, this));

  if (flag) {
    try {
      if (flag & REQUIRED_PARAM && $value === null) {
        throw new TypeError(`Missing parameter value.`);
      }

      if (flag & DURATION_PARAM && $value !== null) {
        try {
          const parsed = Number(parseFloat($value));
          if ((isNaN(parsed)) && (parsed < 60 || parsed > oneYear)) {
            throw "DURATION_ERR";
          }

          return parsed;
        } catch (e) {
          throw __error__('valid expiration period value')
        }
      }

      if (flag & LIST_PARAM) {
        const parser = __parser__.bind(Object.create(null), flag ^ LIST_PARAM);
        return Object.freeze(
          [].concat($value.split(/\s*,\s*/)).filter(Boolean).map(parser)
        );
      }

      if (flag & EMAIL_PARAM) {
        try {
          return matchedString(EMAIL_REGEX, $value).toLowerCase();
        } catch (e) {
          throw __error__('valid email');
        }
      }

      if (flag & URL_PARAM) {
        try {
          return new URL(decodeURIComponent($value)).href;
        } catch (e) {
          throw __error__('valid url');
        }
      }

      if (flag & PUBKEY_PARAM) {
        try {
          return matchedString(PUBKEY_REGEX, $value)
            .toUpperCase()
            .replace(/-[\da-f]{32}-/ig, call(String.prototype.toLowerCase));
        } catch (e) {
          throw __error__('valid public key');
        }
      }

      if (flag & CHECKSUM_PARAM) {
        try {
          return matchedString(CHECKSUM_REGEX, $value).toLowerCase();
        } catch (e) {
          throw __error__('valid checksum value');
        }
      }

      if (flag & COUNTRY_PARAM) {
        try {
          return matchedString(COUNTRY_REGEX, $value).toUpperCase();
        } catch (e) {
          throw __error__('2-character country-like code');
        }
      }

      if (flag & CURRENCY_PARAM) {
        return Currency.defaultValidate($value);
      }

      if (flag & BOOLEAN_PARAM) {
        return resolveBoolean($value, !!(flag & BOOLEAN_DEFAULT_TRUE_PARAM));
      }

      if (flag & NUMERIC_PARAM) {
        try {
          return matchedString(/^[+-]?\d+(?:\.\d+)?$/, $value).slice();
        } catch (e) {
          throw __error__('valid numeric value');
        }
      }

      if (flag & AMOUNT_PARAM) {
        try {
          const parsed = Number(parseFloat($value));
          if (isNaN(parsed) || parsed <= 0) {
            throw "AMOUNT_ERR";
          }

          return parsed;
        } catch (e) {
          throw __error__('amount');
        }
      }

      if (flag & NUMBER_PARAM) {
        try {
          const parsed = Number(parseInt($value));
          if (isNaN(parsed) || parsed <= 0) {
            throw "NUMBER_ERR";
          }

          return parsed;
        } catch (e) {
          throw __error__('number');
        }
      }

      return $value;
    } catch (e) {
      if (flag & REQUIRED_PARAM) throw e;
      return null;
    }
  }

  return $value;
}.bind(Object.create(null)));

const readSearchParams = (function() {
  const __Base64__ = (function(){var t="ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=",n=/^(?:[A-Za-z\d+\/]{4})*?(?:[A-Za-z\d+\/]{2}(?:==)?|[A-Za-z\d+\/]{3}=?)?$/;return Object.freeze(Object.create(null,{encode:{value:function(e){return function(e){for(var n,r,o,c,i="",a=0,d=(e=String(e)).length%3;a<e.length;){if((r=e.charCodeAt(a++))>255||(o=e.charCodeAt(a++))>255||(c=e.charCodeAt(a++))>255)throw new TypeError("Failed to execute 'btoa' on 'Window': The string to be encoded contains characters outside of the Latin1 range.");i+=t.charAt((n=r<<16|o<<8|c)>>18&63)+t.charAt(n>>12&63)+t.charAt(n>>6&63)+t.charAt(63&n)}return d?i.slice(0,d-3)+"===".substring(d):i}(encodeURIComponent(e).replace(/%([0-9A-F]{2})/g,function(e,t){return String.fromCharCode("0x"+t)}))}},decode:{value:function(e){return decodeURIComponent(escape(function(e){if(e=String(e).replace(/[\t\n\f\r ]+/g,""),!n.test(e))throw new TypeError("Failed to execute 'atob' on 'Window': The string to be decoded is not correctly encoded.");e+="==".slice(2-(3&e.length));for(var r,o,c,i="",a=0;a<e.length;)r=t.indexOf(e.charAt(a++))<<18|t.indexOf(e.charAt(a++))<<12|(o=t.indexOf(e.charAt(a++)))<<6|(c=t.indexOf(e.charAt(a++))),i+=64===o?String.fromCharCode(r>>16&255):64===c?String.fromCharCode(r>>16&255,r>>8&255):String.fromCharCode(r>>16&255,r>>8&255,255&r);return i}(e)))}}}))})();

  return function __read__() {
    let querystring;

    try {
      querystring = __Base64__.decode(
        new URLSearchParams(window.location.search).get('__').replace(/\s/g, '+')
      ).replace(/([+])/g, (_, $1) => encodeURIComponent($1));
    } catch (e) {
      querystring = new URLSearchParams(
        window.location.search
          .replace(/([+])/g, (_, $1) => encodeURIComponent($1))
      );
    }

    return new URLSearchParams(querystring);
  };
})();

const SearchParams = (function () {
  const errors = Object.create(null);
  const params = Object.create(null);
  const searchParams = readSearchParams();
  const searchParamsKeys = [...searchParams.keys()];

  const QUERYSTRING = Object.fromEntries(
    [...searchParams.entries()]
      .sort(function __sortParamKeys__([ a ], [ b ]) {
        a = a.toLowerCase();
        b = b.toLowerCase();
        return a > b ? 1 : a < b ? -1 : 0;
      })
  );

  for (const param in PARAMS) {
    let paramValue = String();
    const paramRegex = RegExp(`^${param}$`, 'i');
    const searchParamIndex = searchParamsKeys.findIndex(paramRegex.test.bind(paramRegex));

    if (searchParamIndex >= 0) {
      paramValue = searchParams.get(searchParamsKeys[searchParamIndex]) || paramValue;
      searchParamsKeys.splice(searchParamIndex, 1);
    }

    try { params[param] = __parseParameterValue__(param, paramValue) }
    catch (e) { errors[param] = e.message }
  }

  return Object.freeze(withProperties(Object.create(null), {
    get(...keys) {
      if (!keys.every(isString)) {
        throw new TypeError('Expects parameter name to be a string.');
      }

      const data = keys.reduce(function (data, key) {
        return (data[key] = params[key], data);
      }, Object.create(null));

      return (keys.length === 1) ? data[keys[0]] : data;
    },

    getError() {
      let $error, $param;

      for (const param of Object.keys(errors)) {
        if ($error = errors[$param = param]) break;
      }

      let errorMessage = $error && $error.replace(
        /(?:(parameter) value\.\s*)((?:.*?(?=, but got))?).*$/,
        (_, $1, $2) => `${$1}(\`${$param}\`): Kindly terminate this session and reconfirm the data.`
      );

      const dividedErrorMessage = errorMessage.includes(':') ? errorMessage.split(':') : errorMessage;

      if (dividedErrorMessage[1].includes('Kindly terminate')) {
        if (dividedErrorMessage[0].includes('Missing')) {
          errorMessage = `init.missing.parameter.${$param}`
        } else if (dividedErrorMessage[0].includes('Invalid')) {
          errorMessage = `init.invalid.parameter.${$param}`
        }
      }

      return errorMessage;
    },

    hasErrors: () => !isEmptyObject(errors),
    raw: Object.freeze(QUERYSTRING),
    parsed: Object.freeze(params)
  }));
})();

function __parseError__(expectation) {
  expectation = `${['a', 'e', 'i', 'o', 'u'].indexOf(String(expectation)[0]) >= 0 ? 'an' : 'a'} ${expectation}`;
  return new TypeError(`Invalid parameter value. Expected ${expectation}, but got ${this.value === '' ? "nothing" : `"${this.value}" instead`}.`);
}

function __parseParameterValue__(parameter, value) {
  const flag = parameter in PARAMS ? PARAMS[parameter] : 0;
  return parser(flag, value);
}

function __getSearchParams__(...params) {
  const keysObject = {};

  for (const parameter of params) {
    const [param, key = param] = String(parameter).split('::');
    param && (keysObject[param] = key);
  }

  const paramsObject = SearchParams.get(...Object.keys(keysObject));

  return Object.keys(keysObject).length === 1
    ? paramsObject
    : Object.keys(paramsObject).reduce(function (finalObject, param) {
        finalObject[keysObject[param]] = paramsObject[param];
        return finalObject;
      }, {});
}

export {
  PARAMS,
  parser,
  __getSearchParams__ as getParams
};

export default SearchParams;
